import { hexDistance, hexIndex, hexNeighbors, type HexPos } from './hex'

/**
 * Deterministic A* on the hex grid.
 * Goal: any free hex whose distance to `target` is <= `range`
 * (or the start hex itself, but callers check "already in range" first).
 * `blocked(idx)` marks occupied hexes; start is always allowed.
 * Returns the path as list of hexes EXCLUDING start, or null if unreachable.
 * Determinism: fixed neighbor order, open list sorted by (f, hexIndex).
 */
export function findPath(
  start: HexPos,
  target: HexPos,
  range: number,
  cols: number,
  rows: number,
  blocked: (idx: number) => boolean,
): HexPos[] | null {
  const total = cols * rows
  const startIdx = hexIndex(start, cols)
  const gScore = new Int32Array(total).fill(-1)
  const cameFrom = new Int32Array(total).fill(-1)
  const closed = new Uint8Array(total)

  const heuristic = (p: HexPos): number => {
    const d = hexDistance(p, target)
    return d > range ? d - range : 0
  }

  interface Node { idx: number; pos: HexPos; f: number }
  const open: Node[] = [{ idx: startIdx, pos: start, f: heuristic(start) }]
  gScore[startIdx] = 0

  while (open.length > 0) {
    // deterministic extract-min: lowest f, tiebreak lowest hex index
    let best = 0
    for (let i = 1; i < open.length; i++) {
      const o = open[i]!
      const b = open[best]!
      if (o.f < b.f || (o.f === b.f && o.idx < b.idx)) best = i
    }
    const current = open.splice(best, 1)[0]!
    if (closed[current.idx]) continue
    closed[current.idx] = 1

    if (hexDistance(current.pos, target) <= range && current.idx !== startIdx) {
      // reconstruct path
      const path: HexPos[] = []
      let idx = current.idx
      while (idx !== startIdx) {
        path.push({ col: idx % cols, row: (idx / cols) | 0 })
        idx = cameFrom[idx]!
      }
      path.reverse()
      return path
    }
    if (current.idx === startIdx && hexDistance(current.pos, target) <= range) {
      return [] // already in range
    }

    const g = gScore[current.idx]!
    for (const nb of hexNeighbors(current.pos, cols, rows)) {
      const nIdx = hexIndex(nb, cols)
      if (closed[nIdx] || blocked(nIdx)) continue
      const tentative = g + 1
      if (gScore[nIdx] === -1 || tentative < gScore[nIdx]!) {
        gScore[nIdx] = tentative
        cameFrom[nIdx] = current.idx
        open.push({ idx: nIdx, pos: nb, f: tentative + heuristic(nb) })
      }
    }
  }
  return null
}
